import { parseSubscriptionError, getErrorActionButton } from './subscriptionErrors';

/**
 * Centralized error handling for subscription operations
 * Following DRY principle and Single Responsibility Principle
 */

/**
 * Handle and dispatch subscription error
 * @param {Object} error - Error object or response
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} navigate - React Router navigate function
 * @param {Object} options - Additional options
 * @returns {Object} Parsed error details
 */
export function handleSubscriptionError(error, dispatch, navigate, options = {}) {
  const {
    defaultMessage = 'An error occurred',
    autoRedirect = false,
    confirmRedirect = false,
    redirectActions = ['update_payment', 'cancel_subscription', 'cancel_first'],
    confirmActions = ['update_payment', 'contact_support']
  } = options;

  // Extract error message
  const errorMessage = error?.err || error?.message || error || defaultMessage;
  
  // Parse error to get user-friendly message and action
  const errorDetails = parseSubscriptionError(errorMessage);
  
  // Dispatch error to show notification
  dispatch({
    type: 'HANDLE_RES',
    payload: { err: errorDetails.userFriendlyMessage }
  });
  
  // Handle action button if available
  const actionButton = getErrorActionButton(errorDetails.action, navigate);
  
  if (actionButton) {
    if (autoRedirect && redirectActions.includes(errorDetails.action)) {
      // Auto-redirect after delay
      setTimeout(() => {
        actionButton.handler();
      }, 2000);
    } else if (confirmRedirect && confirmActions.includes(errorDetails.action)) {
      // Show confirmation dialog
      setTimeout(() => {
        const shouldRedirect = window.confirm(
          `${errorDetails.userFriendlyMessage}\n\nWould you like to ${actionButton.text.toLowerCase()}?`
        );
        if (shouldRedirect) {
          actionButton.handler();
        }
      }, 500);
    }
  }
  
  return errorDetails;
}

/**
 * Handle subscription success response
 * @param {Object} response - API response
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} navigate - React Router navigate function
 * @param {Object} options - Additional options
 * @returns {boolean} True if success was handled
 */
export function handleSubscriptionSuccess(response, dispatch, navigate, options = {}) {
  const {
    successMessage = 'Operation completed successfully',
    redirectUrl = '/subscription/success',
    redirectDelay = 1000
  } = options;

  if (!response) return false;

  // Check for successful response
  if (response.statusCode === 200 || response.data) {
    const message = response.data?.message || response.message || successMessage;
    
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: message }
    });
    
    if (redirectUrl) {
      setTimeout(() => {
        navigate(redirectUrl);
      }, redirectDelay);
    }
    
    return true;
  }
  
  return false;
}

/**
 * Wrapper for subscription API calls with unified error handling
 * @param {Function} apiCall - Async function that makes the API call
 * @param {Object} handlers - Object with dispatch, navigate, setLoading functions
 * @param {Object} options - Error/success handling options
 * @returns {Promise<Object>} Response or error details
 */
export async function executeSubscriptionOperation(apiCall, handlers, options = {}) {
  const { dispatch, navigate, setLoading } = handlers;
  
  if (setLoading) setLoading(true);
  
  try {
    const response = await apiCall();
    
    // Try to handle as success
    const wasSuccess = handleSubscriptionSuccess(response, dispatch, navigate, options);
    
    if (!wasSuccess) {
      // Not a clear success, check for errors
      if (response?.err) {
        handleSubscriptionError(response, dispatch, navigate, options);
        if (setLoading) setLoading(false);
        return { error: response.err };
      }
    }
    
    if (setLoading) setLoading(false);
    return { success: true, response };
    
  } catch (error) {
    console.error('Subscription operation failed:', error);
    handleSubscriptionError(error, dispatch, navigate, options);
    if (setLoading) setLoading(false);
    return { error };
  }
}
